// One dashboard card per question: bars for choices, a stacked bar per
// matrix statement, the NPS breakdown, means for numbers and sums, average
// rank, and the most frequent words for text.
import type { OptionCount, QuestionSummary } from '../../api/types';
import { formatNumber, percent } from '../../format';
import { useI18n } from '../../i18n';

/** Ordinal ramp slot (1–5) for column i of n, spread across the ramp. */
function ordSlot(i: number, n: number): number {
  if (n <= 1) return 3;
  return 1 + Math.round((i * 4) / (n - 1));
}

const fmt = (n: number) => formatNumber(Math.round(n * 10) / 10);

function Bars({ options, of, multi }: { options: OptionCount[]; of: number; multi?: boolean }) {
  const { t } = useI18n();
  const max = Math.max(1, ...options.map((o) => o.count));
  return (
    <div className="bar-list">
      {options.map((o) => (
        <div key={o.id} className="bar-row">
          <span className="ellipsis">{o.label}</span>
          <span className="n">
            {fmt(o.count)} · {percent(o.count, of)}
          </span>
          <div className="track" data-tip={`${o.label}: ${fmt(o.count)} (${percent(o.count, of)})`} tabIndex={0} aria-label={`${o.label}: ${fmt(o.count)}`}>
            <div className="fill" style={{ width: `${(o.count / max) * 100}%`, display: o.count ? 'block' : 'none' }} />
          </div>
        </div>
      ))}
      {multi && <p className="hint">{t('resp.multi_note')}</p>}
    </div>
  );
}

function MatrixRows({ q }: { q: QuestionSummary }) {
  const cols = q.rows?.[0]?.options ?? [];
  return (
    <div className="stack gap-12">
      {q.rows!.map((r) => {
        const total = r.options.reduce((a, o) => a + o.count, 0);
        return (
          <div key={r.id} className="stack gap-4">
            <div className="row small">
              <span className="grow ellipsis">{r.label}</span>
              <span className="muted">{fmt(total)}</span>
            </div>
            <div className="segbar" role="img" aria-label={r.options.map((o) => `${o.label} ${percent(o.count, total)}`).join(', ')}>
              {r.options.map((o, i) =>
                o.count > 0 ? (
                  <span key={o.id} style={{ flex: o.count, background: `var(--ord-${ordSlot(i, cols.length)})` }} data-tip={`${o.label}: ${fmt(o.count)} (${percent(o.count, total)})`} tabIndex={0} />
                ) : null,
              )}
            </div>
          </div>
        );
      })}
      <div className="legend">
        {cols.map((c, i) => (
          <span key={c.id}>
            <i style={{ background: `var(--ord-${ordSlot(i, cols.length)})` }} />
            {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Nps({ q }: { q: QuestionSummary }) {
  const { t } = useI18n();
  const n = q.nps!;
  const total = n.promoters + n.passives + n.detractors;
  const parts = [
    { key: 'detractors', label: t('resp.nps_detractors'), value: n.detractors, color: 'var(--div-neg)' },
    { key: 'passives', label: t('resp.nps_passives'), value: n.passives, color: 'var(--div-mid)' },
    { key: 'promoters', label: t('resp.nps_promoters'), value: n.promoters, color: 'var(--div-pos)' },
  ];
  return (
    <div className="stack gap-12">
      <div className="hero-number">
        <span className="value">{n.score > 0 ? `+${fmt(n.score)}` : fmt(n.score)}</span>
        <span className="small muted">{t('resp.nps_score')}</span>
      </div>
      <div className="segbar" role="img" aria-label={parts.map((p) => `${p.label} ${percent(p.value, total)}`).join(', ')}>
        {parts.map((p) => (p.value > 0 ? <span key={p.key} style={{ flex: p.value, background: p.color }} data-tip={`${p.label}: ${fmt(p.value)} (${percent(p.value, total)})`} tabIndex={0} /> : null))}
      </div>
      <div className="legend">
        {parts.map((p) => (
          <span key={p.key}>
            <i style={{ background: p.color }} />
            {p.label} · {fmt(p.value)} ({percent(p.value, total)})
          </span>
        ))}
      </div>
    </div>
  );
}

function ItemMeans({ q, rank }: { q: QuestionSummary; rank?: boolean }) {
  const { t } = useI18n();
  const items = q.items ?? [];
  const max = Math.max(1, ...items.map((i) => i.mean));
  return (
    <div className="bar-list">
      {items.map((i) => (
        <div key={i.id} className="bar-row">
          <span className="ellipsis">{i.label}</span>
          <span className="n">{rank ? t('resp.avg_rank', { n: fmt(i.mean) }) : t('resp.avg_value', { n: fmt(i.mean) })}</span>
          <div className="track" data-tip={`${i.label}: ${fmt(i.mean)}`} tabIndex={0}>
            <div className="fill" style={{ width: `${(rank ? (max - i.mean + 1) / max : i.mean / max) * 100}%` }} />
          </div>
        </div>
      ))}
      <p className="hint">{rank ? t('resp.rank_note') : t('resp.sum_note')}</p>
    </div>
  );
}

function Terms({ terms }: { terms: { word: string; count: number }[] }) {
  const max = Math.max(1, ...terms.map((t) => t.count));
  return (
    <div className="word-cloud" role="list">
      {terms.map((w) => (
        <span key={w.word} role="listitem" style={{ fontSize: `${12 + (w.count / max) * 14}px`, opacity: 0.55 + (w.count / max) * 0.45 }} title={`${w.word}: ${w.count}`}>
          {w.word}
        </span>
      ))}
    </div>
  );
}

/** Whole short answers (names, emails, codes) exactly as typed, most frequent first. */
function Answers({ answers, distinct }: { answers: { word: string; count: number }[]; distinct: number }) {
  const { t } = useI18n();
  return (
    <>
      <ul className="answer-list">
        {answers.map((a) => (
          <li key={a.word}>
            <span className="answer-text">{a.word}</span>
            {a.count > 1 && <span className="pill">{fmt(a.count)}×</span>}
          </li>
        ))}
      </ul>
      <p className="hint">{distinct > answers.length ? t('resp.answers_more', { shown: fmt(answers.length), total: fmt(distinct) }) : t('resp.answers_note')}</p>
    </>
  );
}

export function QuestionCard({ q, n, total }: { q: QuestionSummary; n: number; total: number }) {
  const { t } = useI18n();
  const options = q.options ?? [];
  const optionTotal = options.reduce((a, o) => a + o.count, 0);
  const answeredOf = q.type === 'multiple_choice' ? q.answered : optionTotal || q.answered;
  let body: React.ReactNode;
  if (q.rows?.length) body = <MatrixRows q={q} />;
  else if (q.nps) body = <Nps q={q} />;
  else if (q.items && q.type === 'ranking') body = <ItemMeans q={q} rank />;
  else if (q.items) body = <ItemMeans q={q} />;
  else if (options.length) body = <Bars options={options} of={answeredOf} multi={q.type === 'multiple_choice'} />;
  else if (q.numeric)
    body = (
      <div className="stat-grid">
        {(['mean', 'median', 'min', 'max'] as const).map((k) => (
          <div key={k}>
            <span className="small muted">{t(`resp.stat_${k}`)}</span>
            <strong>{fmt(q.numeric![k])}</strong>
          </div>
        ))}
      </div>
    );
  else if (q.answers?.length) body = <Answers answers={q.answers} distinct={q.distinct ?? q.answers.length} />;
  else if (q.terms?.length)
    body = (
      <>
        <Terms terms={q.terms} />
        <p className="hint">{t('resp.terms_note')}</p>
      </>
    );
  else body = <p className="small muted">{q.answered ? t('resp.no_chart') : t('resp.text_note')}</p>;
  return (
    <div className="card question-card">
      <div className="card-head" style={{ alignItems: 'flex-start' }}>
        <span className="pill">{q.code || n}</span>
        <div className="grow">
          <h2 style={{ fontSize: 15 }}>{q.title || t('common.untitled_question')}</h2>
          <div className="sub">
            {t('resp.answered_summary', { answered: fmt(q.answered), pct: percent(q.answered, total) })}
            {q.numeric && !q.nps && options.length > 0 && t('resp.mean_summary', { mean: fmt(q.numeric.mean), min: fmt(q.numeric.min), max: fmt(q.numeric.max) })}
            {q.looped && ` · ${t('resp.looped_note')}`}
          </div>
        </div>
      </div>
      <div className="card-pad" style={{ paddingTop: 0 }}>
        {body}
        {q.others && q.others.length > 0 && (
          <div className="stack gap-4 mt-12">
            <span className="small muted">{t('resp.other_texts')}</span>
            <div className="row gap-8 wrap">
              {q.others.map((o) => (
                <span key={o.word} className="pill">
                  {o.word} · {o.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
